import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../../contexts/UserContext";
import { useSnackbar } from "notistack";

// MUI Date Pickers
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// MUI Components
import {
  Typography,
  Button,
  TextField,
  FormControl,
  MenuItem,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Grid,
  FormHelperText,
  Box,
  Select,
  CircularProgress,
  RadioGroup,
  Radio,
  Modal,
  Fade,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Local Components
import Header from "../../../components/Header";
import Menu from "../../../components/Menu";
import Footer from "../../../components/Footer";
import ContentContainer from "../../../components/ContentContainer";

// CSS
import "./ContratoDigitalPage.css";

// Importa la función de generación de PDF (basada en pdfMake) desde pdfGenerator.js
import { generatePdfBase64 } from "./pdfGenerator";

// Estructura inicial del formulario extraída como constante
const initialFormState = {
  // DATOS PERSONALES – FACTURACIÓN
  nombre_cliente: "",
  calle_facturacion: "",
  cond_edif_facturacion: "",
  comuna_facturacion: "",
  email: "",
  numero: "",
  tel_trabajo: "",
  // DATOS PERSONALES – CONEXIÓN
  rut_cliente: "",
  rut_representante_legal: "",
  folio: "",
  nro_cliente: "",
  fecha_contrato: null,
  fecha_nacimiento: null,
  depto: "",
  sector: "",
  nodo: "",
  tel_movil: "",
  tel_movil2: "",
  razon_social: "",
  calle_numero: "",
  region_conexion: "",
  comuna_conexion: "",
  tipo_documento: "Boleta",
  envio_documento: "Correo Electrónico",
  autorizacion_envio: false,
  tel_fijo: "",
  // SERVICIOS – PLAN COMERCIAL
  servicio: "Internet",
  plan_servicio: "",
  promocion: "",
  observaciones_plan: "",
  promocion_lista: "",
  velocidad_promocion: "",
  dia_pago: "Día 10 de cada mes",
  primer_pago: null,
  // RENTA MENSUAL
  renta_mensual_plan_comercial: "",
  renta_mensual_servicios_adicionales: "0",
  total_renta_mensual_plan_contratado: "",
  costo_instalacion_servicio_principal: "0",
  costo_instalacion_otros: "0",
  total_costos_instalacion: "",
  total_a_pagar: "",
  plan_seleccionado: "",
  // DOCUMENTOS ACREDITADOS
  doc_fotocopia_constitucion: false,
  doc_fotocopia_rut_representante: false,
  doc_fotocopia_cedula: false,
  doc_comprobante_domicilio: false,
  doc_otro: "",
  doc_condiciones_generales: false,
  doc_contrato_servicios: false,
  doc_contrato_comodato: false,
  doc_informacion_facturacion: false,
};

// Componente de input memoizado
const FormInput = React.memo((props) => (
  <TextField variant="outlined" fullWidth margin="normal" {...props} />
));

const ContratoDigitalPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { roleId, token } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const pdfViewerRef = useRef(null);

  // Obtener información de venta desde state o sessionStorage
  const saleDataFromState = location.state?.saleData;
  const saleDataFromStorage = sessionStorage.getItem("saleData")
    ? JSON.parse(sessionStorage.getItem("saleData"))
    : null;
  const saleData = saleDataFromState || saleDataFromStorage;

  // Estados de carga para catálogos
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingPromotions, setLoadingPromotions] = useState(false);

  // Estado principal del formulario utilizando initialFormState
  const [formData, setFormData] = useState(initialFormState);

  // Estado para el próximo folio disponible
  const [nextFolio, setNextFolio] = useState(null);

  // Catálogos: Regiones, Comunas y Planes Comerciales
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [commercialPlans, setCommercialPlans] = useState([]);

  // Estado para la vista previa del PDF
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const [firmantes, setFirmantes] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState(null);

  // Función auxiliar para obtener el nombre del mes en español
  function obtenerMesEnEspanol(mesNumero) {
    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return meses[mesNumero] || "";
  }

  // Función para obtener el próximo folio disponible
  const fetchNextFolio = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/next-folio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // Usar el nextFolio de la respuesta
        setNextFolio(data.nextFolio);
      }
    } catch (error) {
      console.error("Error al obtener próximo folio:", error);
    }
  }, [token]);
  // Función de autollenado (mantiene todos los campos)
  const handleAutoFill = () => {
    if (saleData) {
      setFormData((prev) => ({
        ...prev,
        nombre_cliente:
          saleData.client_first_name && saleData.client_last_name
            ? `${saleData.client_first_name} ${saleData.client_last_name}`
            : saleData.nombre_cliente || prev.nombre_cliente,
        rut_cliente: saleData.client_rut || prev.rut_cliente,
        rut_representante_legal: saleData.client_rut || prev.rut_representante_legal,
        email: saleData.client_email || prev.email,
        calle_facturacion:
          saleData.street || saleData.calle || prev.calle_facturacion,
        comuna_facturacion:
          saleData.commune && saleData.commune.commune_name
            ? saleData.commune.commune_name
            : saleData.commune_name || prev.comuna_facturacion,
        calle_numero: saleData.street || saleData.calle || prev.calle_numero,
        region_conexion:
          saleData.region && saleData.region.region_id
            ? saleData.region.region_id
            : saleData.region_id || prev.region_conexion,
        comuna_conexion:
          saleData.commune && saleData.commune.commune_id
            ? saleData.commune.commune_id
            : saleData.commune_id || prev.comuna_conexion,
        numero: saleData.number || prev.numero,
        depto:
          saleData.department || saleData.department_office_floor || prev.depto,
        tel_movil: saleData.client_phone || prev.tel_movil,
        nro_cliente: saleData.id_servicio || prev.nro_cliente,
        plan_servicio: saleData.promotion_id || prev.plan_servicio,
      }));
    } else {
      enqueueSnackbar("No se encontraron datos de venta para completar", {
        variant: "warning",
        autoHideDuration: 5000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
    }
  };

  // Función combinada para crear contrato y enviar a Sovos
  const handleSubmitAndSend = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Mostrar indicador de carga
      setIsSubmitting(true);

      // 1. Generar el PDF primero
      console.log("Generando PDF...");
      const pdfBase64 = await generatePdfBase64({
        ...formData,
        folio: formData.folio || nextFolio || "(Pendiente)",
      });

      // 2. Enviar todo en un solo paso al nuevo endpoint
      console.log("Enviando datos...");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/validate-and-create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientRut: formData.rut_cliente,
            clientLegalRepresentativeRut: formData.rut_representante_legal,
            clientEmail: formData.email,
            pdfBase64: pdfBase64,
            contractData: formData,
            selectedSignerId: selectedSignerId,
          }),
        }
      );

      const responseData = await response.json();

      if (responseData.success) {
        enqueueSnackbar("Contrato creado y enviado a Sovos exitosamente", {
          variant: "success",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });

        // Reiniciar el formulario
        setFormData(initialFormState);

        // Actualizar el próximo folio disponible después de enviar
        fetchNextFolio();
      } else {
        enqueueSnackbar(`Error: ${responseData.message}`, {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error en handleSubmitAndSend:", error);
      enqueueSnackbar("Hubo un problema en la creación y envío del contrato", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      // Ocultar indicador de carga
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expande el menú lateral al montar
  useEffect(() => {
    sessionStorage.setItem("menuAdminExpanded", "true");
    // Obtener el próximo folio disponible
    fetchNextFolio();
  }, [fetchNextFolio]);

  // Fetch de Regiones
  const fetchRegions = useCallback(async () => {
    setLoadingRegions(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/regions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        enqueueSnackbar("Error al cargar las regiones", {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const data = await response.json();
      setRegions(data);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error de conexión al cargar regiones", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setLoadingRegions(false);
    }
  }, [token, enqueueSnackbar]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // Carga de Comunas
  const loadCommunes = useCallback(
    async (region) => {
      setLoadingCommunes(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/communes/communes/${region}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          enqueueSnackbar("Error al cargar las comunas", {
            variant: "error",
            autoHideDuration: 5000,
          });
          setCommunes([]);
          return;
        }
        const data = await response.json();
        setCommunes(data);
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Error de conexión al cargar comunas", {
          variant: "error",
          autoHideDuration: 5000,
        });
        setCommunes([]);
      } finally {
        setLoadingCommunes(false);
      }
    },
    [token, enqueueSnackbar]
  );

  useEffect(() => {
    if (formData.region_conexion) {
      loadCommunes(formData.region_conexion);
    }
  }, [formData.region_conexion, loadCommunes]);

  // Fetch de Promociones
  const fetchPromotions = useCallback(async () => {
    if (!formData.comuna_conexion) {
      setCommercialPlans([]);
      return;
    }
    setLoadingPromotions(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/promotions/communes/${formData.comuna_conexion}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        enqueueSnackbar("Error al cargar los planes comerciales", {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setCommercialPlans(data.filter((plan) => plan.is_active));
      } else if (data && Array.isArray(data.current_promotions)) {
        setCommercialPlans(data.current_promotions.filter((plan) => plan.is_active));
      } else {
        enqueueSnackbar("Formato incorrecto en planes comerciales", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error de conexión al cargar promociones", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setLoadingPromotions(false);
    }
  }, [formData.comuna_conexion, token, enqueueSnackbar]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // Autocompletar campos relacionados al plan comercial
  useEffect(() => {
    if (
      formData.plan_servicio &&
      commercialPlans.length > 0 &&
      !formData.promocion
    ) {
      const plan = commercialPlans.find(
        (p) => p.promotion_id === Number(formData.plan_servicio)
      );
      if (plan) {
        const priceRegex = /\$\s*([\d.]+)/;
        const matchPrice = plan.promotion.match(priceRegex);
        let extractedPrice = "$0";
        if (matchPrice && matchPrice[1]) {
          const numericValue = parseInt(matchPrice[1].replace(/\D/g, ""));
          if (!isNaN(numericValue)) {
            extractedPrice = "$" + numericValue;
          }
        }
        let velocity = "";
        const velocityRegex = /(\d+\s*MBPS)/i;
        const matchVelocity = plan.promotion.match(velocityRegex);
        if (matchVelocity && matchVelocity[1]) {
          velocity = matchVelocity[1];
        }
        const velocityAndPrice = velocity
          ? `${velocity} X ${extractedPrice}`
          : extractedPrice;
        setFormData((prev) => ({
          ...prev,
          promocion: plan.promotion,
          renta_mensual_plan_comercial: extractedPrice,
          plan_seleccionado: plan.promotion,
          promocion_lista: "",
          velocidad_promocion: velocityAndPrice,
        }));
      }
    }
  }, [formData.plan_servicio, commercialPlans, formData.promocion]);

  // Cálculo de totales
  const totalRenta = useMemo(() => {
    const plan =
      parseInt(
        (formData.renta_mensual_plan_comercial || "").replace(/\D/g, "")
      ) || 0;
    const servicios =
      parseInt(formData.renta_mensual_servicios_adicionales) || 0;
    return plan + servicios;
  }, [
    formData.renta_mensual_plan_comercial,
    formData.renta_mensual_servicios_adicionales,
  ]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      total_renta_mensual_plan_contratado: `$${totalRenta}`,
    }));
  }, [totalRenta]);

  const totalInstalacion = useMemo(() => {
    const costoPrincipal =
      parseInt(formData.costo_instalacion_servicio_principal) || 0;
    const costoOtros = parseInt(formData.costo_instalacion_otros) || 0;
    return costoPrincipal + costoOtros;
  }, [
    formData.costo_instalacion_servicio_principal,
    formData.costo_instalacion_otros,
  ]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      total_costos_instalacion: totalInstalacion,
    }));
  }, [totalInstalacion]);

  const totalPagar = useMemo(() => {
    const tRenta =
      parseInt(
        formData.total_renta_mensual_plan_contratado.replace(/\D/g, "")
      ) || 0;
    const tInstalacion = parseInt(totalInstalacion) || 0;
    return tRenta + tInstalacion;
  }, [formData.total_renta_mensual_plan_contratado, totalInstalacion]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, total_a_pagar: totalPagar }));
  }, [totalPagar]);

  // Cargar firmantes disponibles
  useEffect(() => {
    const fetchActiveSigners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/active`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setFirmantes(data.data || []);
      } catch (error) {
        console.error("Error al cargar firmantes:", error);
        enqueueSnackbar("Error al cargar firmantes disponibles", {
          variant: "error",
        });
      }
    };

    fetchActiveSigners();
  }, [token, enqueueSnackbar]);

  // Manejo de navegación en el menú lateral
  const handleMenuClick = useCallback(
    (option) => {
      const routes = {
        Ventas: "/ventas",
        "Ingresar venta": "/ingresar-venta",
        Usuarios: "/usuarios",
        "Contratos Digitales": "/contratos",
        Tarifas: "/tarifas",
        Empresas: "/empresas",
        "Canales de venta": "/canales-venta",
        Comunas: "/communes",
        Datos: "/datos",
        Motivos: "/motivos",
        "Mi perfil": "/mi-perfil",
      };
      if (option === "Cerrar sesión") {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/login");
        return;
      }
      sessionStorage.setItem("menuAdminExpanded", "true");
      if (routes[option]) {
        navigate(routes[option]);
      } else {
        console.warn("Opción de menú desconocida:", option);
      }
    },
    [navigate]
  );

  // Manejo de cambios en inputs y fechas
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (newValue, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue ? newValue.toISOString() : null,
    }));
  };

  const validateForm = () => {
    if (!formData.nombre_cliente.trim()) {
      enqueueSnackbar("El nombre del cliente es requerido.", {
        variant: "warning",
        autoHideDuration: 5000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
      return false;
    }
    if (!formData.rut_cliente.trim()) {
      enqueueSnackbar("El RUT del cliente es requerido. Ej: 12345678-9", {
        variant: "warning",
        autoHideDuration: 5000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
      return false;
    }
    if (!formData.rut_representante_legal.trim()) {
      enqueueSnackbar("El RUT del firmante es requerido", {
        variant: "warning",
        autoHideDuration: 5000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
      return false;
    }
    const numericFields = [
      "tel_trabajo",
      "renta_mensual_servicios_adicionales",
      "costo_instalacion_servicio_principal",
      "costo_instalacion_otros",
    ];
    for (let field of numericFields) {
      if (formData[field] && isNaN(parseInt(formData[field]))) {
        enqueueSnackbar(`El campo "${field}" debe ser numérico.`, {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
        return false;
      }
    }
    return true;
  };

  const handlePlanSelect = (e) => {
    const selectedPlanId = e.target.value;
    const plan = commercialPlans.find(
      (p) => p.promotion_id === Number(selectedPlanId)
    );
    if (plan) {
      const priceRegex = /\$\s*([\d.]+)/;
      const matchPrice = plan.promotion.match(priceRegex);
      let extractedPrice = "$0";
      if (matchPrice && matchPrice[1]) {
        const numericValue = parseInt(matchPrice[1].replace(/\D/g, ""));
        if (!isNaN(numericValue)) {
          extractedPrice = "$" + numericValue;
        }
      }
      let velocity = "";
      const velocityRegex = /(\d+\s*MBPS)/i;
      const matchVelocity = plan.promotion.match(velocityRegex);
      if (matchVelocity && matchVelocity[1]) {
        velocity = matchVelocity[1];
      }
      const velocityAndPrice = velocity
        ? `${velocity} X ${extractedPrice}`
        : extractedPrice;
      setFormData((prev) => ({
        ...prev,
        plan_servicio: selectedPlanId,
        promocion: plan.promotion,
        renta_mensual_plan_comercial: extractedPrice,
        plan_seleccionado: plan.promotion,
        promocion_lista: "",
        velocidad_promocion: velocityAndPrice,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        plan_servicio: "",
        promocion: "",
        renta_mensual_plan_contratado: "",
        plan_seleccionado: "",
        velocidad_promocion: "",
      }));
    }
  };

  // Función para enviar el contrato a la base de datos
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );
      if (response.ok) {
        enqueueSnackbar("Contrato creado exitosamente", {
          variant: "success",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
        // Reinicia el formulario usando initialFormState
        setFormData(initialFormState);
        // Actualizar el próximo folio disponible después de enviar
        fetchNextFolio();
      } else {
        enqueueSnackbar("Error al crear el contrato", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error al enviar el formulario:", error);
      enqueueSnackbar("Hubo un problema con la creación del contrato", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  // Función para generar vista previa del PDF con folio provisional
  const handlePreviewPDF = async () => {
    if (!validateForm()) return;

    setIsGeneratingPDF(true);
    try {
      // Usar el folio existente o el nextFolio para la vista previa
      const previewData = {
        ...formData,
        folio: formData.folio || nextFolio || "(Pendiente)",
      };

      const pdfBase64 = await generatePdfBase64(previewData);

      // Convertir Base64 a Blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Crear URL y abrir modal
      const url = URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setPreviewOpen(true);

      enqueueSnackbar("Vista previa generada", {
        variant: "success",
        autoHideDuration: 3000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
    } catch (error) {
      console.error("Error al generar vista previa:", error);
      enqueueSnackbar("Error al generar vista previa", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Función para cerrar modal de vista previa
  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  // Función para descargar PDF desde la vista previa
  const handleDownloadPDF = async () => {
    if (pdfPreviewUrl) {
      const link = document.createElement("a");
      link.href = pdfPreviewUrl;
      link.download = "contrato.pdf";
      link.click();

      enqueueSnackbar("PDF descargado", {
        variant: "success",
        autoHideDuration: 3000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
    } else {
      // Si no hay vista previa, generamos el PDF directamente
      try {
        // Usar el folio existente o el nextFolio para la descarga
        const downloadData = {
          ...formData,
          folio: formData.folio || nextFolio || "(Pendiente)",
        };

        const pdfBase64 = await generatePdfBase64(downloadData);
        const link = document.createElement("a");
        link.href = "data:application/pdf;base64," + pdfBase64;
        link.download = "contrato.pdf";
        link.click();

        enqueueSnackbar("PDF generado y descargado", {
          variant: "success",
          autoHideDuration: 3000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
      } catch (error) {
        console.error("Error al generar PDF:", error);
        enqueueSnackbar("Error al generar PDF", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    }
  };

  // Función para enviar el contrato para firma digital a través del backend
  const handleEnviarFirmaSovos = async () => {
    try {
      if (!formData.rut_cliente || !formData.rut_representante_legal || !formData.email) {
        enqueueSnackbar("⚠️ El RUT y el correo del cliente son obligatorios.", {
          variant: "warning",
          autoHideDuration: 3000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
        return;
      }
      const pdfBase64 = await generatePdfBase64(formData);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/send-for-signature`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientRut: formData.rut_cliente,
            clientLegalRepresentativeRut: formData.rut_representante_legal,
            clientEmail: formData.email,
            pdfBase64: pdfBase64,
            contractType: "Alta",
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        enqueueSnackbar("📄 Documento enviado exitosamente a Sovos.", {
          variant: "success",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
      } else {
        enqueueSnackbar(`❌ Error al enviar documento: ${data.message}`, {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("❌ Error al enviar solicitud:", error);
      enqueueSnackbar("❌ Error de conexión con el servidor.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  // Función para alternar entre seleccionar/deseleccionar todos los documentos
  const handleToggleAllDocuments = () => {
    // Verificar si todos los documentos están seleccionados
    const allSelected =
      formData.doc_fotocopia_constitucion &&
      formData.doc_fotocopia_rut_representante &&
      formData.doc_fotocopia_cedula &&
      formData.doc_comprobante_domicilio &&
      formData.doc_condiciones_generales &&
      formData.doc_contrato_servicios &&
      formData.doc_contrato_comodato &&
      formData.doc_informacion_facturacion;

    // Si todos están seleccionados, deseleccionar todos
    // Si no, seleccionar todos
    setFormData((prev) => ({
      ...prev,
      doc_fotocopia_constitucion: !allSelected,
      doc_fotocopia_rut_representante: !allSelected,
      doc_fotocopia_cedula: !allSelected,
      doc_comprobante_domicilio: !allSelected,
      doc_condiciones_generales: !allSelected,
      doc_contrato_servicios: !allSelected,
      doc_contrato_comodato: !allSelected,
      doc_informacion_facturacion: !allSelected,
    }));
  };

  return (
    <div className="dashboard-container">
      <Header />
      <div className="content-area">
        <Menu
          role_id={roleId}
          onMenuClick={handleMenuClick}
          isAdminExpanded={
            sessionStorage.getItem("menuAdminExpanded") === "true"
          }
          className="menu-sidebar"
        />
        <div className="main-content">
          <ContentContainer>
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 3,
                color: "#99235c",
                fontWeight: 600,
              }}
            >
              Formulario de Contrato Digital
            </Typography>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Button
                variant="contained"
                onClick={handleAutoFill}
                sx={{
                  backgroundColor: "#99235c",
                  color: "#ffffff",
                  textTransform: "uppercase",
                  fontWeight: "500",
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "#7a1c49",
                  },
                }}
              >
                Completar con Datos de Venta
              </Button>
            </Box>
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              {/* --- DATOS PERSONALES – FACTURACIÓN --- */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "#99235c",
                    fontWeight: 500,
                  }}
                >
                  Datos Personales – Facturación
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Nombre del Cliente*"
                      placeholder="Ej: Juan Pérez"
                      name="nombre_cliente"
                      value={formData.nombre_cliente}
                      onChange={handleInputChange}
                      helperText="Ingrese su nombre completo"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Calle"
                      placeholder="Ej: Av. Principal #123"
                      name="calle_facturacion"
                      value={formData.calle_facturacion}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Cond./Edif."
                      placeholder="Ej: Edif. Los Robles"
                      name="cond_edif_facturacion"
                      value={formData.cond_edif_facturacion}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Comuna"
                      placeholder="Ej: Talca"
                      name="comuna_facturacion"
                      value={formData.comuna_facturacion}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Correo Electrónico"
                      placeholder="Ej: correo@dominio.cl"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Número Casa"
                      placeholder="Ej: 123A"
                      name="numero"
                      value={formData.numero}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Teléfono Trabajo"
                      placeholder="Ej: +56912345612"
                      name="tel_trabajo"
                      value={formData.tel_trabajo}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* --- DATOS PERSONALES – CONEXIÓN --- */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "#99235c", // Cambiado de #5c2399 a #99235c
                    fontWeight: 500,
                  }}
                >
                  Datos Personales – Conexión
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="RUT del Cliente*"
                      placeholder="Ej: 12345678-9"
                      name="rut_cliente"
                      value={formData.rut_cliente}
                      onChange={handleInputChange}
                      helperText="Ej: 12345678-9"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Folio"
                      placeholder="Se generará automáticamente al guardar"
                      name="folio"
                      value={
                        formData.folio ||
                        (nextFolio
                          ? `Folio ${nextFolio}`
                          : "Cargando número...")
                      }
                      InputProps={{
                        readOnly: true,
                        sx: {
                          backgroundColor: "rgba(153, 35, 92, 0.05)",
                          fontWeight: nextFolio ? "bold" : "normal",
                          color: nextFolio ? "#333" : "#777",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Número Cliente"
                      placeholder="Ej: 00012"
                      name="nro_cliente"
                      value={formData.nro_cliente}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Fecha del Contrato"
                        inputFormat="YYYY-MM-DD"
                        value={
                          formData.fecha_contrato
                            ? dayjs(formData.fecha_contrato)
                            : null
                        }
                        onChange={(newValue) =>
                          handleDateChange(newValue, "fecha_contrato")
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth margin="normal" />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Fecha de Nacimiento"
                        inputFormat="YYYY-MM-DD"
                        value={
                          formData.fecha_nacimiento
                            ? dayjs(formData.fecha_nacimiento)
                            : null
                        }
                        onChange={(newValue) =>
                          handleDateChange(newValue, "fecha_nacimiento")
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth margin="normal" />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Depto. / Apartamento"
                      placeholder="Ej: 1402"
                      name="depto"
                      value={formData.depto}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Sector"
                      placeholder="Ej: Barrio Norte"
                      name="sector"
                      value={formData.sector}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Nodo"
                      placeholder="Ej: NODO 7"
                      name="nodo"
                      value={formData.nodo}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Teléfono Móvil 1"
                      placeholder="Ej: 123456789"
                      name="tel_movil"
                      value={formData.tel_movil}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Teléfono Móvil 2"
                      placeholder="Ej: +56912345612"
                      name="tel_movil2"
                      value={formData.tel_movil2}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Teléfono Fijo"
                      placeholder="Ej: +56212345678"
                      name="tel_fijo"
                      value={formData.tel_fijo}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Calle y Número (Conexión)"
                      placeholder="Ej: Av. Principal #123"
                      name="calle_numero"
                      value={
                        formData.calle_numero || formData.calle_facturacion
                      }
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Región"
                      select
                      fullWidth
                      name="region_conexion"
                      value={formData.region_conexion}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          region_conexion: e.target.value,
                          comuna_conexion: "",
                        }))
                      }
                      margin="normal"
                    >
                      {loadingRegions ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      ) : (
                        regions.map((region) => (
                          <MenuItem
                            key={region.region_id}
                            value={region.region_id}
                          >
                            {region.region_name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Comuna de Conexión"
                      select
                      fullWidth
                      name="comuna_conexion"
                      value={formData.comuna_conexion}
                      onChange={handleInputChange}
                      margin="normal"
                      disabled={!formData.region_conexion || loadingCommunes}
                    >
                      {loadingCommunes ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      ) : (
                        communes.map((commune) => (
                          <MenuItem
                            key={commune.commune_id}
                            value={commune.commune_id}
                          >
                            {commune.commune_name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              {/* --- MÉTODO DE ENVÍO Y TIPO DE DOCUMENTO --- */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      border: "1px solid #f0f0f0",
                      height: "100%",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: "#99235c",
                        fontWeight: 500,
                      }}
                    >
                      Método de Envío
                    </Typography>
                    <RadioGroup
                      row
                      name="envio_documento"
                      value={formData.envio_documento}
                      onChange={handleInputChange}
                      sx={{
                        justifyContent: "space-around",
                        "& .MuiFormControlLabel-root": {
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1,
                          px: 2,
                          flex: 1,
                          mr: 2,
                          "&:last-child": { mr: 0 },
                        },
                        "& .Mui-checked": {
                          color: "#99235c",
                        },
                      }}
                    >
                      <FormControlLabel
                        value="Correo-e"
                        control={<Radio />}
                        label="Correo Electrónico"
                      />
                      <FormControlLabel
                        value="Correo Postal"
                        control={<Radio />}
                        label="Correo Postal"
                      />
                    </RadioGroup>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      border: "1px solid #f0f0f0",
                      height: "100%",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: "#99235c",
                        fontWeight: 500,
                      }}
                    >
                      Tipo de Documento
                    </Typography>
                    <RadioGroup
                      row
                      name="tipo_documento"
                      value={formData.tipo_documento}
                      onChange={handleInputChange}
                      sx={{
                        justifyContent: "space-around",
                        "& .MuiFormControlLabel-root": {
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1,
                          px: 2,
                          flex: 1,
                          mr: 2,
                          "&:last-child": { mr: 0 },
                        },
                        "& .Mui-checked": {
                          color: "#99235c",
                        },
                      }}
                    >
                      <FormControlLabel
                        value="Boleta"
                        control={<Radio />}
                        label="Boleta"
                      />
                      <FormControlLabel
                        value="Factura"
                        control={<Radio />}
                        label="Factura"
                      />
                    </RadioGroup>
                  </Box>
                </Grid>
              </Grid>

              {formData.tipo_documento === "Factura" && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(153, 35, 92, 0.15)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: "#99235c",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    Datos de Facturación
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Razón Social"
                        fullWidth
                        name="razon_social"
                        value={formData.razon_social}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="RUT"
                        fullWidth
                        name="rut_cliente"
                        value={formData.rut_cliente}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Calle"
                        fullWidth
                        name="calle_facturacion"
                        value={formData.calle_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Número"
                        fullWidth
                        name="numero"
                        value={formData.numero}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Comuna"
                        fullWidth
                        name="comuna_facturacion"
                        value={formData.comuna_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Cond./Edif."
                        fullWidth
                        name="cond_edif_facturacion"
                        value={formData.cond_edif_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Depto"
                        fullWidth
                        name="depto"
                        value={formData.depto}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Sector"
                        fullWidth
                        name="sector"
                        value={formData.sector}
                        onChange={handleInputChange}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* --- SERVICIOS Y PLAN COMERCIAL --- */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "#99235c",
                    fontWeight: 500,
                  }}
                >
                  Servicios – Plan Comercial
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Plan Comercial Disponible"
                      select
                      fullWidth
                      name="plan_servicio"
                      value={formData.plan_servicio}
                      onChange={handlePlanSelect}
                      margin="normal"
                      disabled={
                        !formData.comuna_conexion ||
                        commercialPlans.length === 0
                      }
                    >
                      {loadingPromotions ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      ) : (
                        commercialPlans.map((plan) => (
                          <MenuItem
                            key={plan.promotion_id}
                            value={plan.promotion_id}
                          >
                            {plan.promotion}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormInput
                      label="Promoción"
                      placeholder="Descripción larga"
                      name="promocion"
                      value={formData.promocion}
                      multiline
                      minRows={2}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInput
                      label="Observaciones del Plan"
                      placeholder="Detalles adicionales..."
                      name="observaciones_plan"
                      value={formData.observaciones_plan}
                      onChange={handleInputChange}
                      multiline
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Promoción de Lista"
                      placeholder="(se borra al seleccionar un plan)"
                      name="promocion_lista"
                      value={formData.promocion_lista}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Velocidad + Promoción"
                      placeholder="Ej: 400 MBPS X $9.995"
                      name="velocidad_promocion"
                      value={formData.velocidad_promocion}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Renta Mensual Plan Contratado"
                      name="renta_mensual_plan_comercial"
                      value={formData.renta_mensual_plan_comercial}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* --- RENTA MENSUAL Y CONDICIONES DE PAGO --- */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: "#99235c", // Cambiado de #5c2399 a #99235c
                    fontWeight: 500,
                  }}
                >
                  Renta Mensual y Condiciones de Pago
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Renta Mensual Servicios Adicionales"
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      name="renta_mensual_servicios_adicionales"
                      value={formData.renta_mensual_servicios_adicionales}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Total Renta Mensual Plan Contratado"
                      name="total_renta_mensual_plan_contratado"
                      value={formData.total_renta_mensual_plan_contratado}
                      InputProps={{
                        readOnly: true,
                        sx: { backgroundColor: "rgba(92, 35, 153, 0.05)" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Costo Instalación Servicio Principal"
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      name="costo_instalacion_servicio_principal"
                      value={formData.costo_instalacion_servicio_principal}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Costo Instalación Otros"
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      name="costo_instalacion_otros"
                      value={formData.costo_instalacion_otros}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Total Costos Instalación"
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      name="total_costos_instalacion"
                      value={formData.total_costos_instalacion}
                      InputProps={{
                        readOnly: true,
                        sx: { backgroundColor: "rgba(92, 35, 153, 0.05)" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormInput
                      label="Total a Pagar"
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      name="total_a_pagar"
                      value={formData.total_a_pagar}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          backgroundColor: "rgba(92, 35, 153, 0.05)",
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* --- DOCUMENTOS ACREDITADOS --- */}
              <Box
                className="accredited-documents"
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  background: "#ffffff !important", // Sobreescribir el gradiente del CSS
                  transform: "none !important", // Deshabilitar transformación en hover
                  transition: "none !important", // Deshabilitar transición
                  "&:hover": {
                    transform: "none !important", // Deshabilitar transformación en hover
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05) !important", // Mantener la misma sombra
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#99235c",
                      fontWeight: 500,
                    }}
                  >
                    Documentos Acreditados
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleToggleAllDocuments}
                    sx={{
                      backgroundColor: "#99235c",
                      "&:hover": { backgroundColor: "#7a1c49" },
                      fontSize: "0.85rem",
                      fontWeight: "bold",
                      px: 2,
                    }}
                  >
                    Seleccionar Todos
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_fotocopia_constitucion}
                          onChange={handleInputChange}
                          name="doc_fotocopia_constitucion"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Fotocopia de Constitución"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_fotocopia_rut_representante}
                          onChange={handleInputChange}
                          name="doc_fotocopia_rut_representante"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Fotocopia del RUT del Representante"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_fotocopia_cedula}
                          onChange={handleInputChange}
                          name="doc_fotocopia_cedula"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Fotocopia de la Cédula de Identidad"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_comprobante_domicilio}
                          onChange={handleInputChange}
                          name="doc_comprobante_domicilio"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Comprobante de Domicilio"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Otro Documento (detalle)"
                      placeholder="Describa el documento, si corresponde..."
                      variant="outlined"
                      fullWidth
                      name="doc_otro"
                      value={formData.doc_otro}
                      onChange={handleInputChange}
                      multiline
                      minRows={2}
                      sx={{ mt: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_condiciones_generales}
                          onChange={handleInputChange}
                          name="doc_condiciones_generales"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Acredita Condiciones Generales"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_contrato_servicios}
                          onChange={handleInputChange}
                          name="doc_contrato_servicios"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Acredita Contrato de Servicios"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_contrato_comodato}
                          onChange={handleInputChange}
                          name="doc_contrato_comodato"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Acredita Contrato de Comodato"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.doc_informacion_facturacion}
                          onChange={handleInputChange}
                          name="doc_informacion_facturacion"
                          sx={{
                            color: "#99235c",
                            "&.Mui-checked": { color: "#99235c" },
                          }}
                        />
                      }
                      label="Acredita Información de Facturación"
                    />
                  </Grid>
                </Grid>
              </Box>
            </form>

            {/* Sección de Firmante */}
            <Box
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                backgroundColor: "#fff",
                border: "1px solid #f0f0f0",
                width: "100%", // Ocupar todo el ancho disponible
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "#99235c",
                  fontWeight: 500,
                }}
              >
                Firmantes del documento
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormInput
                      label="RUT Firmante Cliente"
                      placeholder="Ej: 12345678-9"
                      name="rut_representante_legal"
                      value={formData.rut_representante_legal}
                      onChange={handleInputChange}
                      helperText="Ej: 12345678-9"
                    />
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="signer-select-label">Firmante INGBELL</InputLabel>
                    <Select
                      labelId="signer-select-label"
                      id="signer-select"
                      value={selectedSignerId || ""}
                      onChange={(e) => setSelectedSignerId(e.target.value)}
                      label="Firmante INGBELL"
                    >
                      <MenuItem value="">Firmante por defecto</MenuItem>
                      {firmantes.map((firmante) => (
                        <MenuItem key={firmante.id} value={firmante.id}>
                          {firmante.first_name} {firmante.last_name} (
                          {firmante.rut}){firmante.is_manual ? " (Manual)" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {selectedSignerId
                        ? "El documento será firmado por el representante seleccionado"
                        : "Se utilizará el firmante por defecto"}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Sección de acciones */}
            <Box
              sx={{
                textAlign: "center",
                mt: 4,
                mb: 2,
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "#99235c",
                  fontWeight: "bold",
                }}
              >
                Integración con Sovos
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handlePreviewPDF}
                  sx={{
                    px: 4,
                    py: 1.2,
                    minWidth: "200px",
                    backgroundColor: "#ffffff",
                    color: "#99235c",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: "#99235c",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#7a1c49",
                    },
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                  disabled={isGeneratingPDF}
                >
                  {isGeneratingPDF ? (
                    <CircularProgress size={24} sx={{ color: "#99235c" }} />
                  ) : (
                    "Vista Previa"
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitAndSend}
                  disabled={isSubmitting}
                  sx={{
                    px: 4,
                    py: 1.2,
                    minWidth: "200px",
                    backgroundColor: "#99235c",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#7a1c49",
                    },
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Crear Contrato y Enviar a Firmar"
                  )}
                </Button>
              </Box>
            </Box>
          </ContentContainer>
        </div>
      </div>
      <Footer />

      {/* PDF Preview Modal */}
      <Modal
        open={previewOpen}
        onClose={handleClosePreview}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={previewOpen}>
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              width: "90%",
              maxWidth: "850px",
              maxHeight: "90vh",
              borderRadius: 2,
              outline: "none",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" component="h2">
                Vista Previa del Contrato
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  onClick={handleDownloadPDF}
                  sx={{
                    mr: 2,
                    color: "#99235c",
                    borderColor: "#99235c",
                    "&:hover": {
                      borderColor: "#7a1e49",
                      backgroundColor: "rgba(153, 35, 92, 0.04)",
                    },
                  }}
                >
                  Descargar
                </Button>
                <IconButton onClick={handleClosePreview} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                height: "calc(90vh - 100px)",
                overflow: "hidden",
                borderRadius: 1,
                border: "1px solid #eaeaea",
              }}
            >
              {pdfPreviewUrl && (
                <iframe
                  ref={pdfViewerRef}
                  src={pdfPreviewUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="Vista previa del PDF"
                />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default ContratoDigitalPage;
